<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: RadialAncestorChart                                   |
| Section: views/trees                                             |
| Purpose: Radial ancestor chart view for generation exploration.  |
+------------------------------------------------------------------+
-->
<template>
    <div class="RadialAncestorChart_wrapper">
        <!-- Chart container filling the viewport -->
        <div id="family-tree" ref="chartContainer"></div>
    </div>
</template>

<script>
import * as d3 from 'd3';

export default {
    name: 'RadialAncestorChart',
    inject: ['authToken'],
    props: {
        personId: {
            type: String,
            required: true
        },
        selectedGenerations: {
            type: Number,
            default: 6
        }
    },
    data() {
        return {
            descendantsData: null,
            diameter: Math.min(window.innerWidth, window.innerHeight)
        };
    },
    watch: {
        selectedGenerations() {
            if (this.descendantsData) {
                this.renderChart();
            }
        }
    },
    mounted() {
        fetch(`${process.env.VUE_APP_BACKEND_API_URL}/ancestors/${this.personId}`, {
            headers: {
                'X-API-KEY': process.env.VUE_APP_API_KEY
            }
        })
            .then(response => {
                if (!response.ok) throw new Error('Network response was not ok');
                return response.json();
            })
            .then(root => {
                this.descendantsData = root;
                this.renderChart();
            })
            .catch(error => console.error('Error loading data:', error));
        window.addEventListener('resize', this.updateDimensions);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.updateDimensions);
    },
    methods: {
        updateDimensions() {
            this.diameter = Math.min(window.innerWidth, window.innerHeight);
            this.renderChart();
        },
        renderChart() {
            const authToken = this.authToken;
            const diameter = this.diameter;
            const margin = 192;
            const availableRadius = diameter / 2 - margin;
            const maxDepth = this.selectedGenerations - 1;
            const radialScale = d3.scaleLinear()
                .domain([0, maxDepth])
                .range([0, availableRadius]);

            if (this.$refs.chartContainer) {
                this.$refs.chartContainer.innerHTML = "";
            }

            const svg = d3.select(this.$refs.chartContainer)
                .append("svg")
                .attr("width", "100%")
                .attr("height", "100%")
                .attr("viewBox", `0 0 ${diameter} ${diameter}`)
                .attr("preserveAspectRatio", "xMidYMid meet")
                .append("g")
                .attr("transform", `translate(${diameter / 2},${diameter / 2})`);

            const tree = d3.tree()
                .size([2 * Math.PI, availableRadius])
                .separation((a, b) => (a.parent === b.parent ? 1 : 2) / a.depth);

            const radialLink = d3.linkRadial()
                .angle(d => d.x)
                .radius(d => d.y);

            const hierarchy = d3.hierarchy(this.descendantsData, d => d.ancestors)
                .sum(() => 1);

            const nodes = tree(hierarchy);
            nodes.descendants().forEach(d => {
                d.y = radialScale(Math.min(d.depth, maxDepth));
            });

            const displayedNodes = nodes.descendants().filter(d => d.depth <= maxDepth);
            const displayedLinks = nodes.links().filter(link => link.source.depth < maxDepth);

            svg.selectAll(".link")
                .data(displayedLinks)
                .enter()
                .append("path")
                .attr("class", "link")
                .attr("d", radialLink)
                .style("stroke", "rgba(255, 255, 255, 0.24)")
                .style("stroke-width", "1.5px")
                .style("fill", "none")
                .style("opacity", 0)
                .transition()
                .delay(d => d.source.depth * 250)
                .duration(500)
                .style("opacity", 1);

            for (let gen = 1; gen <= maxDepth; gen++) {
                svg.append("circle")
                    .attr("class", "generation-circle")
                    .attr("r", radialScale(gen))
                    .style("fill", "none")
                    .style("stroke", "rgba(255, 255, 255, 0.12)")
                    .style("stroke-width", "0.75px");
            }

            const node = svg.selectAll(".node")
                .data(displayedNodes)
                .enter()
                .append("g")
                .attr("class", "node")
                .attr("transform", d => `
            rotate(${(d.x * 180 / Math.PI) - 90})
            translate(${d.y})
          `)
                .style("opacity", 0);

            node.append("circle")
                .attr("r", 4.5)
                .style("stroke", "var(--brand-text-1)")
                .style("fill", "var(--brand-color-3)")
                .style("stroke-width", "1.5px");

            const clickableLink = node.append("a")
                .attr("xlink:href", d => d.data.placeholder ? null : `/person/${d.data.personID}`)
                .attr("href", d => d.data.placeholder ? null : `/person/${d.data.personID}`)
                .attr("target", "_self")
                .style("pointer-events", "auto");

            clickableLink.append("text")
                .attr("dy", ".31em")
                .attr("text-anchor", d => (d.x < Math.PI ? "start" : "end"))
                .attr("transform", d => (d.x < Math.PI ? "translate(8)" : "rotate(180)translate(-8)"))
                .style("font-size", "12px")
                .style("fill", "#FFF")
                .style("pointer-events", "auto")
                .style("font-family", "var(--font-primary)")
                .attr("class", "clickable-text")
                // Update font-weight and opacity: apply the "restricted" styling only if the person is living and no authToken is present.
                .style("font-weight", d => (d.data.placeholder || (d.data.living === 1 && !authToken)) ? 400 : 700)
                .style("opacity", d => (d.data.placeholder || (d.data.living === 1 && !authToken)) ? 0.48 : 1)
                // Update the text: if the person is living and no authToken, show the privacy message; otherwise show the actual name.
                .text(d => d.data.placeholder
                    ? this.$t('privacy.livingPlaceholder')
                    : (d.data.living === 1 && !authToken)
                        ? this.$t('nav.privacyHidden')
                        : `${d.data.firstname} ${d.data.lastname}`);

            node.each(function () {
                const a = d3.select(this).select("a");
                const textEl = a.select("text").node();
                if (textEl) {
                    const bbox = textEl.getBBox();
                    a.insert("rect", "text")
                        .attr("x", bbox.x)
                        .attr("y", bbox.y)
                        .attr("width", bbox.width)
                        .attr("height", bbox.height)
                        .style("fill", "transparent")
                        .style("pointer-events", "auto");
                }
            });

            node.transition()
                .delay(d => d.depth * 250)
                .duration(500)
                .style("opacity", 1);

            const genLabelsGroup = svg.append("g").attr("class", "gen-labels");
            for (let gen = 1; gen <= maxDepth; gen++) {
                genLabelsGroup.append("text")
                    .attr("class", "generation-label")
                    .attr("dy", "-0.35em")
                    .attr("transform", `translate(0,${-radialScale(gen)})`)
                    .attr("text-anchor", "middle")
                    .style("font-size", "12px")
                    .style("font-weight", "700")
                    .style("fill", "#FFF")
                    .style("opacity", "0.48")
                    .style("pointer-events", "all")
                    .text(`Gen ${gen + 1}`)
                    .on("mouseover", function () {
                        svg.selectAll(".node")
                            .transition()
                            .duration(200)
                            .ease(d3.easeCubicInOut)
                            .style("opacity", d => d.depth === gen ? 1 : 0.12);
                    })
                    .on("mouseout", function () {
                        svg.selectAll(".node")
                            .transition()
                            .duration(200)
                            .ease(d3.easeCubicInOut)
                            .style("opacity", 1);
                    });
            }
        }
    }
};
</script>

<style scoped>
.RadialAncestorChart_wrapper {
    width: 100%;
    min-height: 100vh;
    background: url('@/assets/noise128x128.png'), var(--brand-bg-4);
    background-repeat: repeat;
    background-size: auto;
}

#family-tree {
    width: 100vw;
    height: 100vh;
}

.node circle,
.node rect {
    fill: #fff;
    stroke-width: 1.5px;
}

.node {
    font: 10px sans-serif;
}

.generation-circle {
    fill: none;
    stroke: var(--brand-text-dark);
    stroke-width: 1px;
}

.generation-label {
    font: 8px sans-serif;
    fill: #FFF;
}

.title {
    font: 12px sans-serif;
}

.link {
    fill: none;
    stroke: #aaa;
    stroke-width: 1.5px;
}

.selector {
    margin-bottom: 10px;
    font: 12px sans-serif;
}
</style>
